import type { ActionEntity } from "./types.ts";

const AUTOMATIC_TARGET_LABELS: Record<string, string> = {
  CLOSE_MATCH: "自动投放 · 紧密匹配",
  QUERY_HIGH_REL_MATCHES: "自动投放 · 紧密匹配",
  LOOSE_MATCH: "自动投放 · 宽泛匹配",
  QUERY_BROAD_REL_MATCHES: "自动投放 · 宽泛匹配",
  SUBSTITUTES: "自动投放 · 同类商品",
  ASIN_SUBSTITUTE_RELATED: "自动投放 · 同类商品",
  COMPLEMENTS: "自动投放 · 关联商品",
  ASIN_ACCESSORY_RELATED: "自动投放 · 关联商品"
};

export function automaticTargetLabel(value: string | null | undefined): string | null {
  const normalized = value
    ?.replace(/([a-z])([A-Z])/g, "$1_$2")
    .replace(/[\s-]+/g, "_")
    .toUpperCase();
  return normalized ? AUTOMATIC_TARGET_LABELS[normalized] ?? null : null;
}

export function extractAsinFromAction(action: {
  entity?: ActionEntity | null;
  constrained_value_json?: string | null;
  current_value_json?: string | null;
  proposed_value_json?: string | null;
  evidence_json?: string | null;
  payload_json?: string | null;
  constrained_value?: unknown;
  proposed_value?: unknown;
  payload?: unknown;
  evidence?: unknown;
}): string | null {
  if (action.entity?.asin) return String(action.entity.asin).trim().toUpperCase();
  if (action.entity?.display_label && /^B0[A-Z0-9]{8,9}$/i.test(action.entity.display_label.trim())) {
    return action.entity.display_label.trim().toUpperCase();
  }

  const checkObj = (val: unknown): string | null => {
    if (!val) return null;
    let obj: any = val;
    if (typeof val === "string") {
      try {
        obj = JSON.parse(val);
      } catch {
        return null;
      }
    }
    if (!obj || typeof obj !== "object") return null;
    if (obj.target_asin && typeof obj.target_asin === "string") return obj.target_asin.trim().toUpperCase();
    if (obj.targetAsin && typeof obj.targetAsin === "string") return obj.targetAsin.trim().toUpperCase();
    if (obj.asin && typeof obj.asin === "string") return obj.asin.trim().toUpperCase();
    if (obj.negative_asin && typeof obj.negative_asin === "string") return obj.negative_asin.trim().toUpperCase();
    if (obj.negativeAsin && typeof obj.negativeAsin === "string") return obj.negativeAsin.trim().toUpperCase();
    if (obj.candidate_product?.asin) return String(obj.candidate_product.asin).trim().toUpperCase();
    if (obj.candidateProduct?.asin) return String(obj.candidateProduct.asin).trim().toUpperCase();
    if (Array.isArray(obj.expression) && obj.expression[0]?.value) {
      const v = String(obj.expression[0].value).trim();
      if (/^B0[A-Z0-9]{8,9}$/i.test(v)) return v.toUpperCase();
    }
    const rawSearch = String(obj.rawSearchTerm ?? obj.raw_search_term ?? obj.search_term ?? "");
    const match = /\b(B0[A-Z0-9]{8,9})\b/i.exec(rawSearch);
    if (match) return match[1].toUpperCase();
    return null;
  };

  return (
    checkObj(action.constrained_value ?? action.constrained_value_json) ||
    checkObj(action.proposed_value ?? action.proposed_value_json) ||
    checkObj(action.payload ?? action.payload_json) ||
    checkObj(action.evidence ?? action.evidence_json) ||
    null
  );
}

export function extractKeywordTextFromAction(action: {
  entity?: ActionEntity | null;
  constrained_value_json?: string | null;
  current_value_json?: string | null;
  proposed_value_json?: string | null;
  evidence_json?: string | null;
  payload_json?: string | null;
  constrained_value?: unknown;
  proposed_value?: unknown;
  payload?: unknown;
  evidence?: unknown;
}): string | null {
  if (action.entity?.keyword_text && !action.entity.keyword_text.startsWith("sp_")) {
    return String(action.entity.keyword_text).trim();
  }
  if (action.entity?.display_label && !action.entity.display_label.startsWith("sp_") && action.entity.display_label !== "keyword") {
    return String(action.entity.display_label).trim();
  }

  const checkObj = (val: unknown): string | null => {
    if (!val) return null;
    let obj: any = val;
    if (typeof val === "string") {
      try {
        obj = JSON.parse(val);
      } catch {
        return null;
      }
    }
    if (!obj || typeof obj !== "object") return null;
    if (obj.keyword_text && typeof obj.keyword_text === "string" && !obj.keyword_text.startsWith("sp_")) {
      return String(obj.keyword_text).trim();
    }
    if (obj.keywordText && typeof obj.keywordText === "string" && !obj.keywordText.startsWith("sp_")) {
      return String(obj.keywordText).trim();
    }
    if (obj.keyword && typeof obj.keyword === "string" && !obj.keyword.startsWith("sp_")) {
      return String(obj.keyword).trim();
    }
    if (obj.negative_keyword && typeof obj.negative_keyword === "string") {
      return String(obj.negative_keyword).trim();
    }
    if (obj.ngram && typeof obj.ngram === "string") {
      return String(obj.ngram).trim();
    }
    if (obj.rawSearchTerm && typeof obj.rawSearchTerm === "string" && !/^B0[A-Z0-9]{8,9}$/i.test(obj.rawSearchTerm)) {
      return String(obj.rawSearchTerm).trim();
    }
    if (obj.search_term && typeof obj.search_term === "string" && !/^B0[A-Z0-9]{8,9}$/i.test(obj.search_term)) {
      return String(obj.search_term).trim();
    }
    return null;
  };

  return (
    checkObj(action.constrained_value ?? action.constrained_value_json) ||
    checkObj(action.proposed_value ?? action.proposed_value_json) ||
    checkObj(action.payload ?? action.payload_json) ||
    checkObj(action.evidence ?? action.evidence_json) ||
    null
  );
}

export function isBudgetAction(action?: {
  intent?: string | null;
  proposed_value?: unknown;
  constrained_value?: unknown;
  current_value?: unknown;
  payload?: unknown;
  proposed_value_json?: string | null;
  constrained_value_json?: string | null;
  current_value_json?: string | null;
  payload_json?: string | null;
  resource_type?: string | null;
  operation?: string | null;
} | null): boolean {
  if (!action) return false;
  if (String(action.intent ?? "").toUpperCase().includes("BUDGET")) return true;
  const check = (val: unknown): boolean => {
    if (!val) return false;
    let obj: any = val;
    if (typeof val === "string") {
      try {
        obj = JSON.parse(val);
      } catch {
        return false;
      }
    }
    if (!obj || typeof obj !== "object") return false;
    return obj.daily_budget != null || obj.dailyBudget != null || obj.budget != null;
  };
  return (
    check(action.proposed_value ?? action.proposed_value_json) ||
    check(action.constrained_value ?? action.constrained_value_json) ||
    check(action.current_value ?? action.current_value_json) ||
    check(action.payload ?? action.payload_json)
  );
}

export function isStatePauseAction(action?: {
  proposed_value?: unknown;
  constrained_value?: unknown;
  current_value?: unknown;
  payload?: unknown;
  proposed_value_json?: string | null;
  constrained_value_json?: string | null;
  current_value_json?: string | null;
  payload_json?: string | null;
  intent?: string | null;
  operation?: string | null;
} | null): boolean {
  if (!action) return false;
  if (String(action.operation || "").toLowerCase() === "pause") return true;
  if (String(action.intent || "").toUpperCase().includes("PRUNE") || String(action.intent || "").toUpperCase().includes("PAUSE")) return true;
  const check = (val: unknown): boolean => {
    if (!val) return false;
    let obj: any = val;
    if (typeof val === "string") {
      const upper = val.trim().toUpperCase();
      if (upper === "PAUSED" || upper === "ARCHIVED") return true;
      try {
        obj = JSON.parse(val);
      } catch {
        return false;
      }
    }
    if (!obj || typeof obj !== "object") return false;
    const state = String(obj.state ?? obj.State ?? "").toUpperCase();
    return state === "PAUSED" || state === "ARCHIVED";
  };
  return (
    check(action.proposed_value ?? action.proposed_value_json) ||
    check(action.constrained_value ?? action.constrained_value_json) ||
    check(action.payload ?? action.payload_json)
  );
}

export function extractStateValues(from: unknown, to: unknown): { fromState: string; toState: string } | null {
  const getState = (val: unknown): string | null => {
    if (!val) return null;
    let obj: any = val;
    if (typeof val === "string") {
      const upper = val.trim().toUpperCase();
      if (upper === "ENABLED" || upper === "PAUSED" || upper === "ARCHIVED") return upper;
      try {
        obj = JSON.parse(val);
      } catch {
        return null;
      }
    }
    if (typeof obj === "object" && obj !== null) {
      if (obj.state) return String(obj.state).toUpperCase().trim();
      if (obj.State) return String(obj.State).toUpperCase().trim();
    }
    return null;
  };

  const toState = getState(to);
  if (toState) {
    const fromState = getState(from) || (toState === "PAUSED" ? "ENABLED" : "PAUSED");
    return { fromState, toState };
  }
  return null;
}

export function isPlacementAction(action?: {
  intent?: string | null;
  proposed_value?: unknown;
  constrained_value?: unknown;
  current_value?: unknown;
  payload?: unknown;
  proposed_value_json?: string | null;
  constrained_value_json?: string | null;
  current_value_json?: string | null;
  payload_json?: string | null;
  resource_type?: string | null;
  operation?: string | null;
} | null): boolean {
  if (!action) return false;
  if (String(action.intent ?? "").toUpperCase().includes("PLACEMENT")) return true;
  const check = (val: unknown): boolean => {
    if (!val) return false;
    let obj: any = val;
    if (typeof val === "string") {
      try {
        obj = JSON.parse(val);
      } catch {
        return false;
      }
    }
    if (!obj || typeof obj !== "object") return false;
    return (
      obj.placement_top_percentage != null ||
      obj.placement_product_page_percentage != null ||
      obj.placementTopPercentage != null ||
      obj.placementProductPagePercentage != null ||
      obj.placementBidding != null ||
      obj.placement_bidding != null ||
      (obj.dynamicBidding && typeof obj.dynamicBidding === "object") ||
      (obj.dynamic_bidding && typeof obj.dynamic_bidding === "object")
    );
  };
  return (
    check(action.proposed_value ?? action.proposed_value_json) ||
    check(action.constrained_value ?? action.constrained_value_json) ||
    check(action.current_value ?? action.current_value_json) ||
    check(action.payload ?? action.payload_json)
  );
}

export function extractPlacementPercentages(val: unknown): {
  topPct: number | null;
  prodPct: number | null;
  primaryPct: number | null;
  display: string;
} {
  let topPct: number | null = null;
  let prodPct: number | null = null;

  if (val == null) {
    return { topPct: null, prodPct: null, primaryPct: null, display: "-" };
  }

  let obj: any = val;
  if (typeof val === "string") {
    try {
      obj = JSON.parse(val);
    } catch {
      const match = /(\d+)\s*%/g.exec(val);
      if (match) {
        const p = Number(match[1]);
        return { topPct: p, prodPct: null, primaryPct: p, display: `${p}%` };
      }
      return { topPct: null, prodPct: null, primaryPct: null, display: val };
    }
  }

  if (typeof obj === "number") {
    return { topPct: obj, prodPct: null, primaryPct: obj, display: `${obj}%` };
  }

  if (typeof obj === "object" && obj !== null) {
    if (obj.placement_top_percentage != null) topPct = Number(obj.placement_top_percentage);
    else if (obj.placementTopPercentage != null) topPct = Number(obj.placementTopPercentage);

    if (obj.placement_product_page_percentage != null) prodPct = Number(obj.placement_product_page_percentage);
    else if (obj.placementProductPagePercentage != null) prodPct = Number(obj.placementProductPagePercentage);

    const extractFromList = (list: unknown) => {
      if (Array.isArray(list)) {
        for (const item of list) {
          if (item && typeof item === "object") {
            const p = String(item.placement ?? "").toUpperCase();
            const pct = Number(item.percentage ?? item.percent ?? 0);
            if (p.includes("TOP")) topPct = pct;
            if (p.includes("PRODUCT") || p.includes("PAGE")) prodPct = pct;
          }
        }
      }
    };

    if (obj.placementBidding) extractFromList(obj.placementBidding);
    if (obj.placement_bidding) extractFromList(obj.placement_bidding);
    if (obj.dynamicBidding && typeof obj.dynamicBidding === "object") {
      const dyn = obj.dynamicBidding as Record<string, unknown>;
      if (dyn.placementBidding) extractFromList(dyn.placementBidding);
      if (dyn.placement_bidding) extractFromList(dyn.placement_bidding);
    }
    if (obj.dynamic_bidding && typeof obj.dynamic_bidding === "object") {
      const dyn = obj.dynamic_bidding as Record<string, unknown>;
      if (dyn.placementBidding) extractFromList(dyn.placementBidding);
      if (dyn.placement_bidding) extractFromList(dyn.placement_bidding);
    }
  }

  let primaryPct = topPct ?? prodPct;
  let display = "-";
  if (topPct != null && prodPct != null) {
    if (topPct > 0 && prodPct === 0) display = `${topPct}%`;
    else if (prodPct > 0 && topPct === 0) display = `${prodPct}%`;
    else if (topPct === 0 && prodPct === 0) display = `0%`;
    else display = `首页 ${topPct}% · 详情页 ${prodPct}%`;
  } else if (topPct != null) {
    display = `${topPct}%`;
  } else if (prodPct != null) {
    display = `${prodPct}%`;
  }

  return { topPct, prodPct, primaryPct, display };
}

export function parseNegativeTypeDisplay(val: unknown, entity?: ActionEntity | null, resourceType?: string | null): string {
  const checkType = (raw: unknown): string | null => {
    if (!raw) return null;
    const str = String(raw).toUpperCase();
    if (str.includes("EXACT")) return "精准否定";
    if (str.includes("PHRASE")) return "词组否定";
    if (str.includes("BROAD")) return "广泛否定";
    if (str.includes("ASIN")) return "商品否定";
    return null;
  };

  if (entity?.match_type) {
    const res = checkType(entity.match_type);
    if (res) return res;
  }
  if (entity?.targeting_type) {
    const res = checkType(entity.targeting_type);
    if (res) return res;
  }

  if (val && typeof val === "object") {
    const rec = val as Record<string, unknown>;
    const match = rec.match_type ?? rec.matchType ?? rec.negative_type ?? rec.expression_type ?? rec.expressionType;
    if (match) {
      const res = checkType(match);
      if (res) return res;
    }
    if (rec.asin || rec.negative_asin || rec.target_asin) return "商品否定";
  } else if (typeof val === "string") {
    try {
      const parsed = JSON.parse(val);
      if (parsed && typeof parsed === "object") {
        return parseNegativeTypeDisplay(parsed, entity, resourceType);
      }
    } catch {}
  }

  const resType = String(resourceType ?? "").toLowerCase();
  if (resType.includes("target")) return "商品否定";
  return "精准否定";
}

export function parseTargetingTypeDisplay(
  val: unknown,
  entity?: ActionEntity | null,
  resourceType?: string | null
): string | null {
  const check = (raw: unknown): string | null => {
    if (!raw) return null;
    const str = String(raw).toUpperCase().trim();
    if (str === "EXACT" || str === "TARGETING_EXACT") return "精准匹配";
    if (str === "PHRASE" || str === "TARGETING_PHRASE") return "词组匹配";
    if (str === "BROAD" || str === "TARGETING_BROAD") return "广泛匹配";
    if (str === "ASIN_SAME_AS" || str === "ASIN" || str === "TARGETING_ASIN") return "商品投放";
    if (str === "ASIN_EXPANDED" || str === "ASIN_EXPANDED_FROM") return "商品投放(扩展)";
    if (str === "CLOSE_MATCH" || str === "QUERY_HIGH_REL_MATCHES") return "紧密匹配";
    if (str === "LOOSE_MATCH" || str === "QUERY_BROAD_REL_MATCHES") return "宽泛匹配";
    if (str === "SUBSTITUTES" || str === "ASIN_SUBSTITUTE_RELATED") return "同类商品";
    if (str === "COMPLEMENTS" || str === "ASIN_ACCESSORY_RELATED") return "关联商品";
    return null;
  };

  if (entity?.match_type) {
    const res = check(entity.match_type);
    if (res) return res;
  }
  if (entity?.targeting_type) {
    const res = check(entity.targeting_type);
    if (res) return res;
  }

  if (val && typeof val === "object") {
    const rec = val as Record<string, unknown>;
    const m = rec.match_type ?? rec.matchType ?? rec.targeting_type ?? rec.targetingType;
    if (m) {
      const res = check(m);
      if (res) return res;
    }
    if (rec.target_asin || rec.asin || rec.targetAsin) return "商品投放";
    if (rec.keyword_text || rec.keywordText) return "精准匹配";
  } else if (typeof val === "string") {
    try {
      const parsed = JSON.parse(val);
      if (parsed && typeof parsed === "object") {
        return parseTargetingTypeDisplay(parsed, entity, resourceType);
      }
    } catch {}
  }

  const resType = String(resourceType ?? "").toLowerCase();
  if (resType.includes("keyword")) {
    return "精准匹配";
  }
  if (resType.includes("target")) {
    return "商品投放";
  }
  return null;
}

export function resolveActionEntityLabel(action: {
  entity?: ActionEntity | null;
  resource_type?: string | null;
  operation?: string | null;
  intent?: string | null;
  constrained_value_json?: string | null;
  current_value_json?: string | null;
  proposed_value_json?: string | null;
  evidence_json?: string | null;
  payload_json?: string | null;
  constrained_value?: unknown;
  proposed_value?: unknown;
  payload?: unknown;
  evidence?: unknown;
}): string {
  const resType = String(action.resource_type || "").toLowerCase();
  const op = String(action.operation || "").toLowerCase();

  // 0. Campaign creation & Ad group creation & Product ad creation
  if (resType.includes("campaign") && op === "create") {
    const rec = ((action.constrained_value || action.proposed_value || action.payload) ?? {}) as Record<string, unknown>;
    const name = rec.name ?? rec.campaign_name ?? (action as any).campaign_name;
    if (name) return String(name);
    return "新建广告活动";
  }

  if (resType.includes("ad_group") || resType.includes("adgroup")) {
    const rec = ((action.constrained_value || action.proposed_value || action.payload) ?? {}) as Record<string, unknown>;
    const name = rec.ad_group_name ?? rec.adGroupName;
    if (name) return String(name);
    return "新建广告组";
  }

  if (resType.includes("product_ad") || resType.includes("productad")) {
    const rec = ((action.constrained_value || action.proposed_value || action.payload) ?? {}) as Record<string, unknown>;
    const asin = rec.asin ?? (action.entity?.asin);
    const sku = rec.sku ?? ((action.entity as any)?.sku);
    if (asin) return sku ? `推广商品: ${asin} (${sku})` : `推广商品: ${asin}`;
    return "添加推广商品";
  }

  // 1. Budget adjustment
  if (isBudgetAction(action) || (resType.includes("campaign") && (action.intent?.includes("BUDGET") || resType === "sp_campaigns"))) {
    if (isBudgetAction(action)) return "预算调整";
  }

  // 2. Placement adjustment
  if (isPlacementAction(action)) {
    return "位置溢价调整";
  }

  // 3. Target actions (ASIN or Auto target)
  const isTarget = resType.includes("target") || action.entity?.entity_type === "target";
  if (isTarget) {
    if (action.entity?.asin) return String(action.entity.asin).trim().toUpperCase();
    const extractedAsin = extractAsinFromAction(action);
    if (extractedAsin) return extractedAsin;
    if (action.entity?.targeting_type) {
      const autoLabel = automaticTargetLabel(action.entity.targeting_type);
      if (autoLabel) return autoLabel;
    }
  }

  // 4. Keyword actions (Positive or Negative)
  const isKeyword = resType.includes("keyword") || action.entity?.entity_type === "keyword";
  if (isKeyword) {
    if (action.entity?.keyword_text && !action.entity.keyword_text.startsWith("sp_")) {
      return String(action.entity.keyword_text).trim();
    }
    const extractedKw = extractKeywordTextFromAction(action);
    if (extractedKw) return extractedKw;
  }

  // 5. Check display_label on entity
  const baseLabel = entityDisplayLabel(action.entity, action.resource_type || "广告实体");
  if (baseLabel && !baseLabel.startsWith("sp_") && baseLabel !== "target" && baseLabel !== "keyword" && baseLabel !== "campaign") {
    return baseLabel;
  }

  // 6. Final fallbacks
  const kwFallback = extractKeywordTextFromAction(action);
  if (kwFallback) return kwFallback;

  const asinFallback = extractAsinFromAction(action);
  if (asinFallback) return asinFallback;

  if (resType.includes("campaign")) return "预算调整";
  if (resType.includes("keyword")) return "关键词";
  if (resType.includes("target")) return "投放目标";

  return baseLabel;
}

export function entityDisplayLabel(
  entity: ActionEntity | null | undefined,
  fallback: string,
  context?: { campaign_name?: string | null; decision_reason?: string | null }
): string {
  if (!entity) return fallback;
  if (entity.asin) return entity.asin.trim().toUpperCase();
  const automaticLabel = entity.entity_type === "target" ? automaticTargetLabel(entity.targeting_type) : null;
  const displayLabel = entity.display_label?.trim();
  if (displayLabel && displayLabel.startsWith("sp_") && automaticLabel) return automaticLabel;
  if (automaticLabel && (!displayLabel || displayLabel === entity.entity_id)) return automaticLabel;

  // If display label is purely numeric ID (e.g. 190202555671885), infer clean label
  if (displayLabel && /^\d{10,}$/.test(displayLabel)) {
    if (automaticLabel) return automaticLabel;
    const cName = String(context?.campaign_name || "").toUpperCase();
    if (cName.includes("LOOSE") || cName.includes("宽泛")) return "自动投放 · 宽泛匹配";
    if (cName.includes("CLOSE") || cName.includes("紧密")) return "自动投放 · 紧密匹配";
    if (cName.includes("SUBSTITUTE") || cName.includes("同类")) return "自动投放 · 同类商品";
    if (cName.includes("COMPLEMENT") || cName.includes("关联")) return "自动投放 · 关联商品";
    if (cName.includes("PAT") || cName.includes("COMPETITOR")) {
      const asinMatch = /\b(B0[A-Z0-9]{8,9})\b/i.exec(cName) || /\b(B0[A-Z0-9]{8,9})\b/i.exec(String(context?.decision_reason || ""));
      return asinMatch ? asinMatch[1].toUpperCase() : "ASIN";
    }
  }

  return displayLabel || automaticLabel || fallback;
}

const SHORT_TARGETING_TYPE_LABELS: Record<string, string> = {
  CLOSE_MATCH: "紧密匹配",
  QUERY_HIGH_REL_MATCHES: "紧密匹配",
  LOOSE_MATCH: "宽泛匹配",
  QUERY_BROAD_REL_MATCHES: "宽泛匹配",
  SUBSTITUTES: "同类商品",
  ASIN_SUBSTITUTE_RELATED: "同类商品",
  COMPLEMENTS: "关联商品",
  ASIN_ACCESSORY_RELATED: "关联商品",
  ASIN_SAME_AS: "商品投放",
  ASIN_EXPANDED: "商品投放",
  ASIN_EXPANDED_FROM: "商品投放",
};

export function shortTargetingTypeLabel(value: string | null | undefined): string | null {
  if (!value) return null;
  const str = String(value).trim();
  if (str.includes("紧密匹配")) return "紧密匹配";
  if (str.includes("宽泛匹配")) return "宽泛匹配";
  if (str.includes("同类商品")) return "同类商品";
  if (str.includes("关联商品")) return "关联商品";
  if (str.includes("商品投放")) return "商品投放";
  if (str.includes("ASIN 定向") || str.includes("ASIN定向")) return "商品投放";
  if (str.includes("ASIN 扩展") || str.includes("ASIN扩展")) return "商品投放";
  const normalized = str
    .replace(/([a-z])([A-Z])/g, "$1_$2")
    .replace(/[\s-]+/g, "_")
    .toUpperCase();
  return SHORT_TARGETING_TYPE_LABELS[normalized] ?? str;
}
